import fs from "node:fs";
import path from "node:path";

const SECRET_KEY = /(?:token|secret|password|api[_-]?key|authorization)/iu;

function redact(value: unknown, seen = new WeakSet<object>()): unknown {
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  seen.add(value);
  if (Array.isArray(value)) return value.map((entry) => redact(entry, seen));
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, SECRET_KEY.test(key) ? "[REDACTED]" : redact(entry, seen)]),
  );
}

export type LogLevel = "debug" | "info" | "warn" | "error";

const MAX_RECORD_BYTES = 256 * 1024;

export class Logger {
  readonly filePath: string;

  constructor(
    logsDir: string,
    readonly consoleOutput = true,
    readonly maxBytes = 10 * 1024 * 1024,
    readonly maxFiles = 5,
  ) {
    fs.mkdirSync(logsDir, { recursive: true, mode: 0o700 });
    if (process.platform !== "win32") fs.chmodSync(logsDir, 0o700);
    this.filePath = path.join(logsDir, "gateway.jsonl");
  }

  #rotate(incomingBytes: number): void {
    let size = 0;
    try { size = fs.statSync(this.filePath).size; }
    catch (error) { if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error; }
    if (size + incomingBytes <= this.maxBytes) return;
    for (let index = Math.max(1, this.maxFiles); index >= 1; index -= 1) {
      const source = index === 1 ? this.filePath : `${this.filePath}.${index - 1}`;
      const destination = `${this.filePath}.${index}`;
      try { fs.rmSync(destination, { force: true }); }
      catch { /* best-effort cleanup before an atomic same-filesystem rename */ }
      try { fs.renameSync(source, destination); }
      catch (error) { if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error; }
    }
  }

  log(level: LogLevel, message: string, details?: unknown): void {
    const safeDetails = details === undefined ? undefined : redact(details);
    let record: Record<string, unknown> = {
      ts: new Date().toISOString(),
      level,
      message,
      ...(safeDetails === undefined ? {} : { details: safeDetails }),
    };
    let line = `${JSON.stringify(record)}\n`;
    if (Buffer.byteLength(line) > MAX_RECORD_BYTES) {
      const preview = JSON.stringify(safeDetails).slice(0, 16 * 1024);
      record = { ts: record.ts, level, message, details: { truncated: true, preview } };
      line = `${JSON.stringify(record)}\n`;
    }
    this.#rotate(Buffer.byteLength(line));
    fs.appendFileSync(this.filePath, line, { mode: 0o600 });
    if (process.platform !== "win32") fs.chmodSync(this.filePath, 0o600);
    if (this.consoleOutput) {
      const output = level === "error" ? process.stderr : process.stdout;
      output.write(`[${String(record.ts)}] ${level.toUpperCase()} ${message}${record.details === undefined ? "" : ` ${JSON.stringify(record.details)}`}\n`);
    }
  }

  tail(lines = 100): Array<Record<string, unknown>> {
    const count = Math.max(1, Math.min(Math.trunc(lines), 1_000));
    try {
      return fs
        .readFileSync(this.filePath, "utf8")
        .trim()
        .split("\n")
        .slice(-count)
        .filter(Boolean)
        .map((line) => JSON.parse(line) as Record<string, unknown>);
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw error;
    }
  }
}
